import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from 'react-toastify';

// Get initial state from localStorage if available
const initialState = {

  items: [],
  totalAmount: 0,
};

// Define initial state
// const initialState = getInitialState();

// Define max quantity for cart items
const MAX_QUANTITY = 20;

export const fetchCartAsync = createAsyncThunk(
  "cart/fetchCartAsync",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) return null;
      const response = await axios.get(`https://api.swasthmitra.com/api/cart/viewCart/${userId}`);
      const formatData = {
        items: response.data.items.map((item) => ({
          productID: item.medicine._id,
          productName: item.medicine.name,
          dealRate: item.medicine.dealRate,
          gst: item.medicine.gst,
          mrp: item.medicine.mrp,
          salt: item.medicine.salt || 'N/A',
          batchNumber: item.medicine.batchNumber,
          packof: item.medicine.packOf,
          productImage: item?.medicine?.imageUrl || item?.medicine?.imageUrls[0],
          quantity: item.quantity,
        })),
        totalAmount: response.data.totalAmount
      }
      return formatData;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch cart');
    }
  }
);

// Asynchronous thunk to add item to the cart
export const addToCartAsync = createAsyncThunk(
  "cart/addToCartAsync",
  async ({ userId, productID, quantity }, { rejectWithValue }) => {
    try {
      if (!userId) {
        toast.error("Please login to add items to cart");
        return;
      }
      const response = await axios.post("https://api.swasthmitra.com/api/cart/addToCart", { userId, medicineId: productID, quantity });
      // return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Asynchronous thunk to remove item from the cart
export const removeFromCartAsync = createAsyncThunk(
  "cart/removeFromCartAsync",
  async ({ userId, productID }, { rejectWithValue }) => {
    try {
      const response = await axios.delete("https://api.swasthmitra.com/api/cart/removeItem",
        { data: { userId, medicineId: productID } }
      );
      // return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Asynchronous thunk to update item quantity in the cart
export const updateQuantityAsync = createAsyncThunk(
  "cart/updateQuantityAsync",
  async ({ userId, productID, quantity }, { rejectWithValue }) => {
    console.log(userId, productID, quantity);
    try {
      const response = await axios.put("https://api.swasthmitra.com/api/cart/updateQuantity", { userId, medicineId: productID, quantity });
      console.log(response)
      // return response.data;
    } catch (error) {
      console.log(error)
      return rejectWithValue(error.response.data);
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Optimistic reducer for adding to the cart (frontend update)
    addToCart(state, action) {
      const product = action.payload;
      const existingItem = state.items.find((item) => item.productID === product.productID);
      if (existingItem) {
        if (existingItem.quantity < MAX_QUANTITY) {
          existingItem.quantity += 1;
          state.totalAmount += product?.dealRate;
        }
      } else {
        state.items.push({ ...product, quantity: 1 });
        state.totalAmount += product?.dealRate;
      }
    },
    // Optimistic reducer for updating item quantity
    updateQuantity(state, action) {
      const { productID, quantity } = action.payload;
      const itemToUpdate = state.items.find((item) => item.productID === productID);
      if (itemToUpdate) {
        const difference = quantity - itemToUpdate.quantity;
        if (quantity <= MAX_QUANTITY) {
          itemToUpdate.quantity = quantity;
          state.totalAmount += difference * itemToUpdate.dealRate;
        } else {
          itemToUpdate.quantity = MAX_QUANTITY;
          state.totalAmount += (MAX_QUANTITY - itemToUpdate.quantity) * itemToUpdate.dealRate;
        }
      }
    },
    // Optimistic reducer for removing an item from the cart
    removeFromCart(state, action) {
      const productId = action.payload;
      const itemToRemove = state.items.find((item) => item.productID === productId);
      if (itemToRemove) {
        state.totalAmount -= itemToRemove.dealRate * itemToRemove.quantity;
        state.items = state.items.filter((item) => item.productID !== productId);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.totalAmount = 0;
    },
    // Add a new reducer to sync with localStorage
    syncWithLocalStorage: (state) => {
      // try {
      //   if (state.items && Array.isArray(state.items)) {
      //     localStorage.setItem('cart', JSON.stringify({
      //       items: state.items,
      //       totalAmount: state.totalAmount
      //     }));
      //   }
      // } catch (error) {
      //   console.error('Error saving cart to localStorage:', error);
      // }
    },
    // Add a hydrate reducer to load state from localStorage
    hydrateCart: (state, action) => {
      // try {
      //   const savedCart = action.payload;
      //   if (savedCart && Array.isArray(savedCart.items)) {
      //     state.items = savedCart.items;
      //     state.totalAmount = savedCart.totalAmount || 0;
      //   }
      // } catch (error) {
      //   console.error('Error hydrating cart:', error);
      // }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart Items
      .addCase(fetchCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.items = action.payload.items || [];
          state.totalAmount = action.payload.totalAmount || 0;
          // Sync with localStorage
          // localStorage.setItem('cart', JSON.stringify({
          //   items: state.items,
          //   totalAmount: state.totalAmount
          // }));
        }


        state.loading = false;
      })
      .addCase(fetchCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // On error, try to restore from localStorage
        // try {
        //   const savedCart = localStorage.getItem('cart');
        //   if (savedCart) {
        //     const parsedCart = JSON.parse(savedCart);
        //     state.items = parsedCart.items || [];
        //     state.totalAmount = parsedCart.totalAmount || 0;
        //   }
        // } catch (error) {
        //   console.error('Error restoring cart from localStorage:', error);
        // }
      })
      // Add to Cart
      .addCase(addToCartAsync.pending, (state) => {
        // state.loading = true;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        // const product = action.payload.product;
        // const existingItem = state.items.find((item) => item?.productID === product?.productID);

        // if (existingItem) {
        //   if (existingItem.quantity < MAX_QUANTITY) {
        //     existingItem.quantity += 1;
        //     state.totalAmount += product?.productPrice;
        //   }
        // } else {
        //   state.items.push({ ...product, quantity: 1 });
        //   state.totalAmount += product?.productPrice;
        // }

        // state.loading = false;
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove from Cart
      .addCase(removeFromCartAsync.pending, (state) => {
        // state.loading = true;
      })
      .addCase(removeFromCartAsync.fulfilled, (state, action) => {
        // state.items = action.payload.items;
        // state.totalAmount = action.payload.totalAmount || 0;
        // state.loading = false;
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Update Quantity
      .addCase(updateQuantityAsync.pending, (state) => {
        // state.loading = true;
      })
      .addCase(updateQuantityAsync.fulfilled, (state, action) => {
        // state.items = action.payload.items;
        // state.totalAmount = action.payload.totalAmount;
        // state.loading = false;
      })
      .addCase(updateQuantityAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  syncWithLocalStorage,
  hydrateCart
} = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items;
export const selectCartTotalAmount = (state) => {
  if (!state.cart.items || !Array.isArray(state.cart.items)) {
    return 0;
  }

  const total = state.cart.items.reduce((sum, item) => {
    const price = parseFloat(item.dealRate) || 0;
    const quantity = parseInt(item.quantity) || 0;
    // Calculate price without GST as per user requirement
    return sum + (price * quantity);
  }, 0);

  return Number(total.toFixed(2));
};

export default cartSlice.reducer;